import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { BsArrowDown } from "react-icons/bs";
import { VscOpenPreview } from "react-icons/vsc";
import { MdHighlightOff } from "react-icons/md";
import _ from "lodash";
import ButtonPermission from "../../components/UI/Button/ButtonPermission";
import funcionalidades from "../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import EfisModalJustificativa from "../../components/UI/EfisModal/EfisModalJustificativa";
import TextSpinner from "../../components/UI/Spinner/TextSpinner";
import IE from "../../components/Formatters/IE";
import ButtonImage from "../../components/UI/Button/ButtonImage";
import useSetGuard from "../../hooks/useSetGuard";
import useHomologarSpf from "./hooks/useHomologarSpf";
import useBuscaSafePayload from "./hooks/useBuscaSafePayload";
import useAssinar from "./hooks/useAssinar";
import constants from "../Constants";

const procedimentosCell = (row) => {
  if (row.procedimentos.length <= 0) {
    return "";
  }

  return (
    <div>
      {row.procedimentos.map((item) => (
        <li>{item}</li>
      ))}
    </div>
  );
};

const acoesCell = (row, setShowAlterarConfirm) => (
  <>
    {funcionalidades.spf.revisar && (
      <ButtonImage
        className="btn-lg text-success pa-0 p-0"
        buttonImage={<VscOpenPreview />}
        tooltipImage="Revisar"
        onClick={() => {
          setShowAlterarConfirm({
            show: true,
            codigo: row.numero,
            situacao: "R",
            body: `Revisar SPF número ${row.numero}`,
          });
        }}
      />
    )}
    {funcionalidades.spf.cancelar && (
      <ButtonImage
        className="btn-lg text-danger pa-0 p-0"
        buttonImage={<MdHighlightOff />}
        tooltipImage="Cancelar"
        onClick={() => {
          setShowAlterarConfirm({
            show: true,
            codigo: row.numero,
            situacao: "C",
            body: `Cancelar SPF número ${row.numero}`,
          });
        }}
      />
    )}
  </>
);

// const columns = [
//   {
//     name: "Nº SPF",
//     cell: (row) => row.numero,
//     maxWidth: "6%",
//     sortable: true,
//   },
//   {
//     name: "Ciclo",
//     cell: (row) => (row.nomeCiclo === null ? "" : row.nomeCiclo),
//     maxWidth: "15%",
//   },
//   {
//     name: "Inscrição Estadual",
//     cell: (row) => (row.inscricaoEstadual === "" ? " " : <IE value={row.inscricaoEstadual} />),
//     maxWidth: "10%",
//     center: true,
//   },
//   {
//     name: "Razão Social",
//     cell: (row) => row.nomeContribuinte,
//     maxWidth: "20%",
//     sortable: true,
//   },
//   {
//     name: "Setor",
//     cell: (row) => row.nomeOrgao,
//     maxWidth: "10%",
//   },
//   {
//     name: "Tipo de Ação Fiscal",
//     cell: (row) => row.descricaoTipoAcaoFiscal,
//     maxWidth: "13%",
//   },
//   {
//     name: "Procedimentos",
//     cell: procedimentosCell,
//     maxWidth: "22%",
//   },
// ];

const HomologaProcedimentoFiscal = ({ pageSizeRows = 10 }) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeRows);
  const [pageNumber, setPageNumber] = useState(1);
  const [selection, setSelection] = useState({});
  const [showAlterarConfirm, setShowAlterarConfirm] = useState({
    show: false,
    codigo: 0,
    situacao: null,
    body: null,
  });

  const hasFetchedOnce = useRef(false); // 🔒 Evita disparar consulta 2x na montagem

  const setGuardClient = useSetGuard();

  const { isLoadingHomologar, dataHomologar, errorFormHommologar, homologaSpf } = useHomologarSpf({ selection });

  const { errorSafePayload, buscaSafePayload, safePayloadAgrupador } = useBuscaSafePayload();

  const { assinaturaHandler, resetarEnvio } = useAssinar({ setGuardClient });

  const { request: requestConsulta, data: dataConsulta, errors: errorConsulta, isLoading } = useApiClient();

  const { request: requestRevisar, data: dataRevisar, errors: errorRevisar } = useApiClient();

  const { request: requestCancelar, data: dataCancelar, errors: errorCancelar } = useApiClient();

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  const consultaHomologacao = useCallback(() => {
    requestConsulta(apiRoutes.fiscalizacao.procedimentoFiscal.consultaHomologaProcedimentoFiscal, {
      pageSize,
      pageNumber,
    });
  }, [requestConsulta, pageSize, pageNumber]);

  //  -------------------------

  // Colunas memoizadas (não recriam a cada render)
  const columns = useMemo(() => {
    return [
      {
        name: "Nº SPF",
        cell: (row) => row.numero,
        maxWidth: "6%",
        sortable: true,
      },
      {
        name: "Ciclo",
        cell: (row) => (row.nomeCiclo === null ? "" : row.nomeCiclo),
        maxWidth: "15%",
      },
      {
        name: "Inscrição Estadual",
        cell: (row) => (row.inscricaoEstadual === "" ? " " : <IE value={row.inscricaoEstadual} />),
        maxWidth: "10%",
        center: true,
      },
      {
        name: "Razão Social",
        cell: (row) => row.nomeContribuinte,
        maxWidth: "20%",
        sortable: true,
      },
      {
        name: "Setor",
        cell: (row) => row.nomeOrgao,
        maxWidth: "10%",
      },
      {
        name: "Tipo de Ação Fiscal",
        cell: (row) => row.descricaoTipoAcaoFiscal,
        maxWidth: "13%",
      },
      {
        name: "Procedimentos",
        cell: procedimentosCell,
        maxWidth: "22%",
      },
      {
        name: "Ações",
        center: true,
        maxWidth: "4%",
        cell: (row) => acoesCell(row, setShowAlterarConfirm),
      },
    ];
  }, [setShowAlterarConfirm]);

  //  Chama consulta apenas uma vez na montagem (evita loop)
  useEffect(() => {
    if (!hasFetchedOnce.current && pageSize && pageNumber) {
      consultaHomologacao();
      hasFetchedOnce.current = true;
    }
  }, [consultaHomologacao, pageSize, pageNumber]);
  // --------------------------

  // if (_.last(columns).name === "Ações") {
  //   columns.pop();
  // }

  // columns.push({
  //   name: "Ações",
  //   center: true,
  //   maxWidth: "4%",
  //   cell: (row) => acoesCell(row, setShowAlterarConfirm),
  // });

  // useEffect(() => {
  //   consultaHomologacao();
  // }, [consultaHomologacao]);

  useEffect(() => {
    if (dataConsulta && dataConsulta.result) {
      if (dataConsulta.result.length && !dataConsulta.result[0].totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados.`);
        return;
      }
      setRows(dataConsulta.result || []);
      setTotalRows(dataConsulta.result.length ? dataConsulta.result[0].totalRows : 0);
    }

    if (errorConsulta) {
      toast.error(errorConsulta.message);
    }
  }, [dataConsulta, errorConsulta]);

  const chamaRevisarCancelar = (values) => {
    if (values.situacaoAberturaProcesso === "R") {
      requestRevisar(apiRoutes.fiscalizacao.procedimentoFiscal.revisaProcedimentoFiscal, values);
    } else if (values.situacaoAberturaProcesso === "C") {
      requestCancelar(apiRoutes.fiscalizacao.procedimentoFiscal.cancelaProcedimentoFiscal, values);
    } else {
      toast.error("Apenas possível Revisar ou Cancelar.");
    }
  };

  // Revisão de SPF
  useEffect(() => {
    if (!dataRevisar && !errorRevisar) return;

    if (dataRevisar) {
      toast.success("SPF enviada para Revisão com sucesso.");
      consultaHomologacao();
    }

    if (errorRevisar) {
      toast.error(errorRevisar.message);
    }
    setShowAlterarConfirm({ show: false });
  }, [dataRevisar, errorRevisar, setShowAlterarConfirm, consultaHomologacao]);

  // Cancelamento de SPF
  useEffect(() => {
    if (dataCancelar) {
      toast.success("SPF cancelada com sucesso.");
      consultaHomologacao();
    }

    if (errorCancelar) {
      toast.error(errorCancelar.message);
    }
    setShowAlterarConfirm({ show: false });
  }, [dataCancelar, errorCancelar, setShowAlterarConfirm, consultaHomologacao]);

  // Envia os tickets para retornar o safePayloadAgrupador
  useEffect(() => {
    if (dataHomologar) {
      buscaSafePayload(dataHomologar.result);
    }
    if (errorFormHommologar) {
      toast.error(errorFormHommologar.message);
    }
  }, [errorFormHommologar, buscaSafePayload, dataHomologar]);

  // safePayloadAgrupador para abrir assinador
  useEffect(() => {
    if (safePayloadAgrupador) {
      resetarEnvio();
      assinaturaHandler(safePayloadAgrupador);
    }

    if (errorSafePayload) {
      toast.error(errorSafePayload.message);
    }
  }, [errorSafePayload, safePayloadAgrupador, assinaturaHandler, resetarEnvio]);

  if (isLoadingHomologar) {
    return (
      <span>
        <Spinner animation="border" role="status" />
        Preparando documento para assinatura...
      </span>
    );
  }

  return (
    <>
      <EfisModalJustificativa
        show={showAlterarConfirm.show}
        body={showAlterarConfirm.body}
        codigo={showAlterarConfirm.codigo}
        situacao={showAlterarConfirm.situacao}
        loadingConfirmationAction={isLoading}
        closeHandler={() => setShowAlterarConfirm({ show: false })}
        confirmationHandler={(codigo, situacao, texto) => {
          chamaRevisarCancelar({ numero: codigo, situacaoAberturaProcesso: situacao, textoJustificativa: texto });
        }}
      />

      {setGuardClient.guardLink}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-body">
              <DataTable
                noHeader
                title=""
                keyField="numero"
                columns={columns}
                data={rows}
                progressPending={isLoading}
                noDataComponent="Nenhum registro para exibir"
                progressComponent={<TextSpinner />}
                defaultSortFieldId={1}
                sortIcon={<BsArrowDown />}
                pagination
                sortable
                selectableRows
                onSelectedRowsChange={(state) => {
                  setSelection(state.selectedRows);
                }}
                pointerOnHover
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                paginationPerPage={pageSize}
                paginationTotalRows={totalRows}
                paginationComponentOptions={constants.paginationComponentOptions}
                onRowClicked={(state) => {
                  window.open(`/app/spf/pendencias/detalhe?numSpf=${state.numero}`, "_self");
                }}
              />
            </div>

            <div className="card-footer pt-3 pb-0 flex-row w-100">
              <Form.Group as={Row} className="d-flex flex-row">
                <Col sm="12" className="form-inline justify-content-start">
                  <ButtonPermission
                    className="mr-0 btn btn-primary btn-md btn-user"
                    name="Assinar"
                    permission={funcionalidades.spf.homologar}
                    onClick={() => homologaSpf()}
                  />
                  &nbsp;
                </Col>
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomologaProcedimentoFiscal;
